export type ChatCitation = { label: string; id?: string };

/** A follow-up the assistant offers as a button. */
// fork(oem): the local engine's "which report?" branch returns six choices.
export type ChatMessageAction = { label: string; prompt: string };

export type ChatMessagePart =
  | { type: 'markdown'; content: string }
  | { type: 'tool'; verb: string; preview: string; detail?: string }
  | { type: 'artifact'; title: string; kind: string }
  // fork(oem): a disabled card for a surface that is on the roadmap.
  | { type: 'notice'; title: string; kind: string; subtitle: string; note: string }
  // fork(oem): clickable follow-up prompts.
  | { type: 'actions'; actions: ChatMessageAction[] }
  // fork(L3): a generated report — its generation steps, then its card.
  | {
      type: 'report';
      /** The message id, so the steps animate once per answer, not per remount. */
      runId: number;
      kind: string;
      title: string;
      subtitle: string;
      label: string;
      format: string;
      download: string | null;
      route: string;
      steps: { label: string; detail: string }[];
    };

export type ChatMessage = {
  id: number;
  who: 'user' | 'bot';
  time: string;
  body: string;
  citations?: ChatCitation[];
  parts?: ChatMessagePart[];
};

export type ChatThread = {
  id: string;
  title: string;
  pinned: boolean;
  updatedAt: number;
  messages: ChatMessage[];
  /** Present on project-scoped threads (see thread-seed.ts). */
  projectSlug?: string;
};

export type ThreadNoticeSeverity = 'info' | 'warning' | 'critical';

export type ThreadNotice = {
  id: string;
  kind: string;
  severity: ThreadNoticeSeverity;
  body: string;
  actionHint?: string;
};

export type ComposerStyleId = 'default' | 'concise' | 'formal';

export const COMPOSER_STYLE_LABELS: Record<ComposerStyleId, string> = {
  default: 'Default',
  concise: 'Concise',
  formal: 'Formal',
};

export const NEW_THREAD_ID = 'thread-new';

/** Messages kept visible after manual compact — shared by engine + plus-menu. */
export const TAIL_PRESERVE_MESSAGE_COUNT = 2;

export type PermissionMode = 'default' | 'bypassPermissions';

export type ModelProvider = 'anthropic' | 'google' | 'openai';

/**
 * fork(oem v1.8): `'local'` is the ProjeX local engine — no LLM. Kept off
 * `ModelProvider`, which the design-system `ProviderLogo` maps exhaustively.
 */
export type ChatModelProvider = ModelProvider | 'local';

export type ModelOption = {
  id: string;
  label: string;
  shortLabel: string;
  description: string;
  provider: ChatModelProvider;
};

export const DEFAULT_MODEL_ID = 'claude-opus-4-8';

/**
 * fork(oem v1.8): what the chat composer offers — the one engine that
 * actually answers. The OEM+ assistant is the deterministic local engine
 * (`shared/assistant/local-answer.ts`); the provider catalogue below was a
 * picker whose choice never reached it, which read as "Claude Opus answered"
 * to a reviewer. One entry, so `ModelSelector` renders a static label.
 */
export const LOCAL_ENGINE_MODEL_ID = 'projex-local';

export const CHAT_MODELS: ModelOption[] = [
  {
    id: LOCAL_ENGINE_MODEL_ID,
    label: 'ProjeX local engine',
    shortLabel: 'ProjeX local engine',
    description: 'deterministic · runs on your fleet data',
    provider: 'local',
  },
];

/**
 * Composer model catalog — mirrors ai-foundation `src/lib/models.ts` MODELS.
 *
 * fork(oem v1.8): no longer offered in the chat composer (see
 * {@link CHAT_MODELS}); kept as the foundation admin console's catalogue
 * (`modules/admin/AiModelsScene.transform.ts`).
 */
export const SEED_MODELS: ModelOption[] = [
  {
    id: 'claude-opus-4-8',
    label: 'Claude Opus 4.8',
    shortLabel: 'Opus 4.8',
    description: 'Most capable. Best for complex reasoning and long-running tasks.',
    provider: 'anthropic',
  },
  {
    id: 'claude-sonnet-4-6',
    label: 'Claude Sonnet 4.6',
    shortLabel: 'Sonnet 4.6',
    description: 'Balanced speed and intelligence. Good default.',
    provider: 'anthropic',
  },
  {
    id: 'claude-haiku-4-5',
    label: 'Claude Haiku 4.5',
    shortLabel: 'Haiku 4.5',
    description: 'Fastest and cheapest. Best for simple turns.',
    provider: 'anthropic',
  },
  {
    id: 'gemini-3.5-flash',
    label: 'Gemini 3.5 Flash',
    shortLabel: 'Gemini Flash',
    description: 'Fast Gemini model via Google ADK (feature-flagged).',
    provider: 'google',
  },
  {
    id: 'gemini-3.1-pro-preview',
    label: 'Gemini 3.1 Pro',
    shortLabel: 'Gemini Pro',
    description: 'Capable Gemini model via Google ADK (feature-flagged).',
    provider: 'google',
  },
  {
    id: 'gemini-3.1-flash-lite',
    label: 'Gemini 3.1 Flash-Lite',
    shortLabel: 'Gemini Flash-Lite',
    description:
      'Most cost-efficient — high-volume agentic tasks and simple processing (via Google ADK, feature-flagged).',
    provider: 'google',
  },
];
